import * as React from "react";
import { connect } from "react-redux";
import { Modal } from "react-bootstrap";
import Loader from "@/modules/shared/ui/Loader";
import { translate } from "@/modules/shared/IntlProviderWrapper";
import { getBackend as getBackend2 } from "@/lib/backend/backend2";
import { ORDER_TYPE, PLAN_IMPORT_TYPE } from "@/lib/ManufacturingConsts.generated";
import ErrorComponent from "@/modules/shared/ui/ErrorComponent";
import * as BusinessLogic from "@/lib/businessLogic";
import { JOB_STATUS } from "@/lib/CommonConsts.generated";

import * as t from "@/lib/backend/manufacturing2.generated.types";
import type { IGetJobStatusRes } from "@/lib/backend/common.generated.types";
import type { ReduxState } from "@/modules/pg/shared/planningTable/reducers/reducers";

type OwnProps = {
    import_type: string;
    plant_external_id: string;
    line_group_uuid: string;
    onCloseImportPlanOnDemand: () => void;
};

type Props = OwnProps & ReduxProps;

type State = {
    loading: boolean;
    error: string;
    success: boolean;
    job_status: IGetJobStatusRes | null;
};

export class ImportPlanOnDemandJobHandler {
    static timeout: ReturnType<typeof setTimeout> | null = null;

    static async runImportPlanOnDemand(
        data: t.IRunImportPlanOnDemandReq,
        onSuccess: () => void,
        onFailure: (e: string) => void,
        onUpdate: (status: IGetJobStatusRes) => void
    ) {
        const res = await getBackend2().manufacturing.runImportPlanOnDemand(data);
        await ImportPlanOnDemandJobHandler.getImportPlanStatus(res.job_uuid, onSuccess, onFailure, onUpdate);
    }

    static clearTimeout = () => {
        if (ImportPlanOnDemandJobHandler.timeout) {
            clearTimeout(ImportPlanOnDemandJobHandler.timeout);
        }
    };

    static async getImportPlanStatus(
        job_uuid: string,
        onSuccess: (result: any) => void,
        onFailure: (e: string) => void,
        onUpdate: (status: IGetJobStatusRes) => void
    ) {
        try {
            ImportPlanOnDemandJobHandler.clearTimeout();
            const res = await getBackend2().common.getJobStatus({ id: job_uuid });
            onUpdate(res);
            const status = res.status;
            if (status === JOB_STATUS.ended || status === JOB_STATUS.errored) {
                const res2 = await getBackend2().manufacturing.getImportPlanOnDemandResult({ job_uuid });
                if (status === JOB_STATUS.errored) {
                    return onFailure("Import failed due to an internal error");
                } else {
                    return onSuccess(res2.result);
                }
            } else {
                ImportPlanOnDemandJobHandler.timeout = setTimeout(() => {
                    ImportPlanOnDemandJobHandler.getImportPlanStatus(job_uuid, onSuccess, onFailure, onUpdate);
                }, 1500);
            }
        } catch (e) {
            console.error(e);
            onFailure((e as Error).message);
        }
    }
}

export class ImportPlanOndemand extends React.Component<Props, State> {
    state: State = {
        success: false,
        error: "",
        job_status: null,
        loading: false,
    };

    componentDidMount() {
        this.setState({
            success: false,
            error: "",
            job_status: null,
            loading: false,
        });
    }

    componentWillUnmount() {
        ImportPlanOnDemandJobHandler.clearTimeout();
    }

    getImportPlanOndemandData = (): t.IRunImportPlanOnDemandReq => {
        const order_ids = new Set<string>();
        const plan_order_ids = new Set<string>();

        for (const line of this.props.lines_orders) {
            if (this.props.extra_lines?.some(l => l.line === line.line_uuid)) continue;
            for (const order of line.orders) {
                if (order.order_type === ORDER_TYPE.plan) {
                    plan_order_ids.add(order.external_id);
                } else {
                    order_ids.add(order.external_id);
                }
            }
        }

        for (const unscheduled_line of this.props.unscheduled_orders) {
            for (const unscheduled_order of unscheduled_line.orders) {
                if (unscheduled_order.order_type === ORDER_TYPE.plan) {
                    plan_order_ids.add(unscheduled_order.external_id);
                } else {
                    order_ids.add(unscheduled_order.external_id);
                }
            }
        }

        const import_type = this.props.import_type;
        if (import_type == PLAN_IMPORT_TYPE.lists) {
            return {
                order_ids: [...order_ids],
                plan_order_ids: [...plan_order_ids],
                import_type: PLAN_IMPORT_TYPE.lists,
                plant_external_id: this.props.plant_external_id,
            };
        } else if (import_type == PLAN_IMPORT_TYPE.plant) {
            const plan_import_weeks = Math.floor(
                BusinessLogic.getLineGroupTagInt(this.props.line_group_uuid, "planning_table_horizon_days", 7 * 3) / 7
            );
            return {
                import_type: PLAN_IMPORT_TYPE.plant,
                plan_import_weeks,
                plant_external_id: this.props.plant_external_id,
            };
        } else {
            throw new Error("Unsupported import_type: " + this.props.import_type);
        }
    };

    onImportSuccess = () => {
        this.setState({ loading: false, success: true, error: "" });
        ImportPlanOnDemandJobHandler.clearTimeout();
        this.setState({ loading: false });
    };

    onImportFailure = (e: string) => {
        this.setState({ loading: false, error: e });
    };

    onImportUpdate = (e: IGetJobStatusRes) => {
        this.setState({ job_status: e });
    };

    onImportPlanOnDemand = async () => {
        this.setState({ loading: true });
        try {
            const data = this.getImportPlanOndemandData();
            await ImportPlanOnDemandJobHandler.runImportPlanOnDemand(
                data,
                this.onImportSuccess,
                this.onImportFailure,
                this.onImportUpdate
            );
        } catch (e) {
            this.setState({ loading: false, error: (e as Error).message });
        }
    };

    getModalBody = () => {
        const { job_status } = this.state;
        const status =
            job_status && job_status.status_msg && job_status.status_msg !== "UNKNOWN" ? (
                <ErrorComponent
                    msg={job_status.status_msg}
                    type={job_status.status === JOB_STATUS.errored ? "error" : "info"}
                    onlyDiv
                />
            ) : null;

        if (this.state.loading) {
            return (
                <>
                    {status}
                    <div style={{ marginTop: "-50px" }}>
                        <Loader small />
                    </div>
                </>
            );
        }

        if (this.state.success) {
            return (
                <>
                    {status}
                    <ErrorComponent
                        msg={translate("common.successfully_import_erp", "Data successfully imported from ERP")}
                        type="success"
                        onlyDiv
                    />
                </>
            );
        }

        if (this.state.error) {
            return (
                <>
                    {status}
                    <ErrorComponent msg={this.state.error} type="error" onlyDiv={true} />
                </>
            );
        }

        return translate(
            "common.are_you_sure",
            "This action will overwrite your current changes. Are you sure you want to continue?"
        );
    };

    getButtons = () => {
        if (this.state.success) {
            return (
                <button className="btn btn-primary" onClick={this.onRefresh}>
                    {translate("common.refresh", "Refresh")}
                </button>
            );
        }

        if (this.state.error) {
            return (
                <button className="btn btn-primary" onClick={this.props.onCloseImportPlanOnDemand}>
                    {translate("common.ok", "Ok")}
                </button>
            );
        }

        const save_disabled = this.props.lol_is_loading || this.state.loading;

        return (
            <>
                <button className="btn btn-primary" onClick={this.onImportPlanOnDemand} disabled={save_disabled}>
                    {translate("common.yes", "Yes")}
                </button>
                <button
                    className="btn btn-primary"
                    onClick={this.props.onCloseImportPlanOnDemand}
                    disabled={save_disabled}
                >
                    {translate("common.no", "No")}
                </button>
            </>
        );
    };

    onRefresh = () => {
        window.location.reload();
    };

    render() {
        return (
            <Modal show={true} onHide={this.props.onCloseImportPlanOnDemand} style={{ top: "100px" }}>
                <Modal.Header>
                    <Modal.Title>
                        <span>
                            {this.props.import_type == PLAN_IMPORT_TYPE.lists
                                ? translate("common.import_orders", "Refresh from ERP order in shop floor")
                                : translate("common.import_orders_plant", "Refresh from ERP order in plant")}
                        </span>
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body>{this.getModalBody()}</Modal.Body>

                <Modal.Footer>
                    <div style={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
                        {this.getButtons()}
                    </div>
                </Modal.Footer>
            </Modal>
        );
    }
}

const mapState = (state: ReduxState) => {
    const unscheduled_orders = state.gantt_chart_lines_orders.unscheduled_orders;
    const extra_lines = state.gantt_chart_report.extra_lines;
    const lines_orders = state.gantt_chart_lines_orders.lines_orders;
    const lol_is_loading = state.gantt_chart_lines_orders.loading;

    return {
        lol_is_loading,
        lines_orders,
        unscheduled_orders,
        extra_lines,
    };
};

type ReduxProps = ReturnType<typeof mapState>;

export default connect<ReduxProps, {}, OwnProps, ReduxState>(mapState, {})(ImportPlanOndemand);
